import { Sun } from "lucide-react";

import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { useWalletStatusActions } from "../hooks/useWalletStatusActions";
import type { RuntimeWallet } from "../types/payments-wallets.types";
import { WalletTargetSummary } from "./WalletTargetSummary";

export interface UnfreezeWalletDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * The wallet being unfrozen. Taken whole so the dialog can name its target and show the reason it is about
   * to erase: the backend clears `frozen_reason` on unfreeze and keeps no actor trace (no `frozen_by` /
   * `frozen_at`, no wallet status-change log), so unfreezing the wrong wallet destroys the review note with
   * nothing to reconstruct it from.
   */
  wallet: RuntimeWallet;
  /** Called after a successful unfreeze so the caller can refetch the wallet. */
  onUnfrozen: () => void;
}

/**
 * Confirm unfreezing a wallet (INTERNAL staff, `payments:write`). Shared by the wallets home cards and the
 * statement hero so both surfaces guard the in-flight request identically.
 */
export function UnfreezeWalletDialog({ open, onClose, wallet, onUnfrozen }: UnfreezeWalletDialogProps) {
  const { t } = useTranslations("wallets");
  const { unfreeze, isSubmitting } = useWalletStatusActions();

  const handleConfirm = async () => {
    try {
      await unfreeze(wallet.id);
      colocaToast.success(t("statement_unfreeze_success"));
      onUnfrozen();
      onClose();
    } catch (err: unknown) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("statement_unfreeze_error"));
    }
  };

  return (
    <ColocaAlertDialog
      open={open}
      // `AlertDialogAction` is a plain button — confirming never dismisses, this handler owns closing. Only a
      // success closes the dialog; a failure keeps it open so the operator can retry. Guarding on
      // `isSubmitting` also stops Escape / outside-click from detaching it from an in-flight request.
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onClose();
      }}
      media={<Sun className="size-8 text-warning" />}
      title={t("statement_unfreeze_title_wallet", { currency: wallet.currency })}
      description={t("statement_unfreeze_desc")}
      confirmLabel={t("statement_unfreeze")}
      cancelLabel={t("statement_freeze_cancel")}
      confirmDisabled={isSubmitting}
      onConfirm={() => void handleConfirm()}
    >
      <div className="space-y-3">
        <WalletTargetSummary wallet={wallet} />
        {wallet.frozen_reason && (
          <p className="text-xs text-muted-foreground">
            {t("statement_unfreeze_clears_reason")}{" "}
            <span className="font-medium text-foreground">{wallet.frozen_reason}</span>
          </p>
        )}
      </div>
    </ColocaAlertDialog>
  );
}
